/** TypeScript type definitions for script analysis results. */

import type { MandateOverview } from './mandateOverview';

export interface ScoreComponent {
  raw_score: number;
  weighted_score: number;
  max_points: number;
}

export interface GenreAffect {
  raw_score: number;
  note?: string;
}

/** Score weightings from Firestore (config/score_weightings). */
export interface ScoreWeightings {
  story_spine: number;
  content_pacing: number;
  general_thematic_clarity: number;
  authenticity: number;
  character_resonance: number;
  genre_accuracy: number;
  scene_strength: number;
  dialogue: number;
  [key: string]: number;
}

/** Weighted contribution per component (from scoring calculator). */
export interface WeightedBreakdown {
  story_spine?: number;
  content_pacing?: number;
  general_thematic_clarity?: number;
  authenticity?: number;
  character_resonance?: number;
  genre_accuracy?: number;
  scene_strength?: number;
  dialogue?: number;
}

export interface Scorecard {
  overall_engagement_score: number;
  max_possible_score: number;
  engagement_percentage: number;
  /** Main genre identified from analysis (e.g. "Thriller", "Drama"). */
  main_genre?: string;
  /** One-sentence logline per Echo Index (problem-solving arc); displayed under script title. */
  logline?: string;
  /** Structural synopsis from Agent U; expandable on the scorecard. */
  synopsis?: string;
  etb_structure: ScoreComponent;
  conceptual_relevance: ScoreComponent;
  emotional_transformation: ScoreComponent;
  genre_affect?: GenreAffect;
  /** Scene strength percentage (0-100, Creator only). */
  scene_strength?: number;
  /** Dialogue strength percentage (0-100, Creator only). */
  dialogue_strength?: number;
  /** Weightings used for calculation (from Firestore). */
  score_weightings?: ScoreWeightings;
  /** Weighted contribution per component. */
  weighted_breakdown?: WeightedBreakdown;
  /** Raw scores per component (for display). */
  component_scores?: Record<string, number | null | undefined>;
  /** Deterministic pillar + sub-metric scores (V7 canonical shape). */
  v7_scores?: {
    story_spine_pct?: number | null;
    character_strength_pct?: number | null;
    theme_strength_pct?: number | null;
    commercial_viability_pct?: number | null;
    scene_strength_pct?: number | null;
    etb_structure_pct?: number | null;
    pacing_pct?: number | null;
    story_density_pct?: number | null;
    story_beat_strength_pct?: number | null;
    concentration_pct?: number | null;
    character_concentration_pct?: number | null;
    character_resonance_pct?: number | null;
    character_active_pct?: number | null;
    dialogue_strength_pct?: number | null;
    character_journey_pct?: number | null;
    thematic_quality_pct?: number | null;
    authenticity_pct?: number | null;
    genre_promise_pct?: number | null;
    originality_pct?: number | null;
    thematic_stakes_pct?: number | null;
    target_market_pct?: number | null;
    originality_excluded_from_calculation?: boolean;
    originality_matched_film?: string | null;
  };
  /** Read-only legacy fallback: scorecards saved before the V7 rename only have this key. */
  v6_scores?: Scorecard["v7_scores"];
  /** When true, 0% originality was excluded from Commercial Viability / overall score. */
  originality_excluded_from_calculation?: boolean;
  /** Matched produced film when originality is excluded (Agent V duplication rule). */
  originality_matched_film?: string | null;
  /** User-facing notice when originality is excluded from the main calculation. */
  originality_exclusion_notice?: string | null;
  /** Deterministic pillar percentages (convenience for display). */
  story_spine_pct?: number | null;
  character_strength_pct?: number | null;
  theme_strength_pct?: number | null;
  commercial_viability_pct?: number | null;
  /** Beat-level attention series (0–100), 12 points typical. */
  attention_curve?: number[] | null;
}

/** Returns true when deterministic pillar scores are available. */
export function hasDeterministicPillarScores(scorecard: Scorecard | null | undefined): scorecard is Scorecard {
  if (!scorecard) return false;
  if (scorecard.v7_scores && typeof scorecard.v7_scores === "object") return true;
  // Read-only legacy fallback for scorecards saved before the V7 rename.
  if (scorecard.v6_scores && typeof scorecard.v6_scores === "object") return true;
  return (
    scorecard.story_spine_pct != null ||
    scorecard.character_strength_pct != null ||
    scorecard.theme_strength_pct != null ||
    scorecard.commercial_viability_pct != null
  );
}

export interface Pitfall {
  category: string;
  issue: string;
  severity: "Critical" | "Warning" | "Optimization" | string;
  timestamp?: string;
  scene_number?: number;
}

/** Echo Index V7 blueprint split into three user-facing sections (legacy). */
export interface BlueprintSections {
  /** All scores (overall + pillars + sub-metrics). Legacy echo_index_v7. */
  scorecard?: string | null;
  /** Per sub-metric: what works (summary), what does not, actionable notes. Legacy. */
  summary?: string | null;
  /** Full output of each agent. */
  full?: string | null;
  /** Nuclius Index v1 sections */
  story_overview?: string | null;
  mandate?: string | null;
  engagement?: string | null;
  commerciality?: string | null;
  recommendation?: string | null;
}

/** Nuclius Index five-section scorecard (primary user-facing report). */
export interface NucliusScorecardSections {
  story_overview?: string | null;
  mandate?: string | null;
  engagement?: string | null;
  commerciality?: string | null;
  recommendation?: string | null;
  full?: string | null;
}

export interface NucliusStructuredRecommendation {
  body_markdown: string;
  recommendation_label?: string;
  engagement_pct?: number;
  commerciality_pct?: number | null;
  mandate_score?: number | null;
}

export interface NucliusStructuredEngagementMetric {
  id?: string;
  title?: string;
  pillar?: string;
  score_pct?: number | null;
  overall_feedback?: string;
  detailed_feedback?: string;
}

export interface NucliusStructuredEngagement {
  body_markdown?: string;
  engagement_pct?: number;
  story_spine_pct?: number | null;
  character_strength_pct?: number | null;
  theme_strength_pct?: number | null;
  metrics?: NucliusStructuredEngagementMetric[];
}

export interface NucliusStructuredCommercialityBlock {
  overall_feedback?: string;
  detailed_feedback?: string;
}

export interface NucliusStructuredCommerciality {
  body_markdown?: string;
  commercial_viability_pct?: number | null;
  originality_pct?: number | null;
  target_market_pct?: number | null;
  originality?: NucliusStructuredCommercialityBlock;
  target_market?: NucliusStructuredCommercialityBlock;
  commercial_viability?: NucliusStructuredCommercialityBlock;
}

export interface NucliusStructuredMandateCategory {
  category?: string;
  category_key?: string;
  user_mandate?: string;
  result?: string;
  feedback?: string;
  points?: number | null;
}

export interface NucliusStructuredMandate {
  body_markdown?: string;
  mandate_score?: number | null;
  category_ratings?: NucliusStructuredMandateCategory[];
  overall_feedback?: string;
}

export interface NucliusSectionsStructured {
  story_overview?: Record<string, unknown>;
  mandate?: NucliusStructuredMandate;
  engagement?: NucliusStructuredEngagement;
  commerciality?: NucliusStructuredCommerciality;
  recommendation?: NucliusStructuredRecommendation;
}

export function isNucliusBlueprint(blueprint: Blueprint | null | undefined): boolean {
  if (!blueprint) return false;
  if (blueprint.format === 'nuclius_index_v1') return true;
  const s = blueprint.sections;
  return !!(s?.story_overview || s?.recommendation);
}

export interface Blueprint {
  identified_pitfalls?: Pitfall[];
  action_steps?: string[];
  /** For Decision Maker audience: commission/finance recommendation (quality, risks, recommend/not recommend). */
  commission_summary?: string | null;
  /** For Creator audience: encouraging message about rewriting, optionally with a verified quote (displayed at end of analysis). */
  encouraging_quote?: string | null;
  /** Echo Index V7 canonical user-facing blueprint markdown (all sections concatenated). */
  blueprint_markdown?: string | null;
  /** Echo Index V7 blueprint split into collapsible sections. */
  sections?: BlueprintSections | null;
  /** Structured section payloads (Nuclius Index v1). */
  sections_structured?: NucliusSectionsStructured | null;
  format?: string | null;
}

export interface ScriptMetadata {
  script_title?: string;
  format?: string;
  total_duration_minutes?: number;
  total_scenes?: number;
}

export interface ParsedScript {
  metadata?: ScriptMetadata;
  parsed_script?: any[];
}

export interface AgentFinding {
  agent_name: string;
  findings: any;
  score?: number;
}

export interface AgentFindings {
  [agent_name: string]: AgentFinding;
}

export interface InteractionHistoryEntry {
  action: "user_query" | "agent_response" | string;
  timestamp?: string;
  query?: string;
  response?: string;
  agent?: string;
}

export interface AnalysisResults {
  script_title?: string | null;
  parsed_script?: ParsedScript | null;
  scorecard?: Scorecard | null;
  blueprint?: Blueprint | null;
  /** Top-level V7 blueprint markdown (also inside blueprint.blueprint_markdown). */
  blueprint_markdown?: string | null;
  agent_findings?: AgentFindings;
  interaction_history?: InteractionHistoryEntry[];
  /** "creator" | "decision_maker" – used for audience-specific labels and title */
  audience_type?: string | null;
  /** Raw agent outputs (for Creator sectioned display). */
  etb_analysis?: string | null;
  conceptual_analysis?: string | null;
  emotional_analysis?: string | null;
  genre_analysis?: string | null;
  /** Scene Strength analysis (Creator only). */
  scene_analysis?: string | null;
  /** Dialogue assessment (Creator only). */
  dialogue_analysis?: string | null;
  /** Development mandate fit — independent of Echo Index engagement score. */
  mandate_overview?: MandateOverview | null;
}

export interface UploadResponse {
  status: "success" | "error" | "methodology_pending";
  session_id: string;
  script_text?: string;
  gcs_path?: string;
  gcs_uri?: string;
  canonical_gcs_path?: string;
  canonical_format?: string;
  filename?: string;
  script_name?: string;
  corpus_name?: string;
  methodology_status?: string;
  message?: string;
  error?: string;
}

export interface AnalyzeResponse {
  status: "success" | "error";
  session_id: string;
  message?: string;
  error?: string;
  gcs_uri?: string;
}

export interface ResultsResponse {
  status: "success" | "error";
  session_id: string;
  results: AnalysisResults;
  error?: string;
}

export interface StatusResponse {
  session_id: string;
  status: "completed" | "processing" | "error" | "not_found";
  error?: string;
  warning?: string;
  status_detail?: string;
  /** Agent names / methodology agent_ids that have completed */
  completed_agents?: string[];
  /** Pipeline phase / step ids completed (authoritative for workflow stepper when SSE is quiet) */
  completed_phases?: string[];
  /** Methodology completion keys (e.g. 1.7.3 or A:1.7.3) */
  completed_step_ids?: string[];
  failed_step_ids?: string[];
  current_step_id?: string;
  /** Agent currently executing (from Firestore progress when client reconnects via poll) */
  current_agent?: string;
  /** Multi-strand progress */
  strand_id?: string;
  strand_phase?: string;
  multi_strand_status?: string;
  strand_ids?: string[];
  is_multi_strand?: boolean;
  dominance_band?: string;
  framework?: string;
  scorecard_pending?: boolean;
  errors?: string[];
}

/** SSE events from POST /api/scripts/analyze/stream */
export type StreamEvent =
  | {
      type: "agent_start";
      agent_name: string;
      started_at: string;
      step_id?: string;
      strand_id?: string;
    }
  | {
      type: "agent_end";
      agent_name: string;
      started_at: string;
      ended_at: string;
      duration_seconds: number;
      step_id?: string;
      strand_id?: string;
      failed?: boolean;
    }
  | { type: "started"; session_id: string }
  | { type: "complete"; session_id: string; status?: string }
  | { type: "methodology_pending"; session_id?: string; message?: string; status?: string; code?: string }
  | {
      type: "methodology_start";
      session_id: string;
      script_id?: string;
      framework?: string;
      methodology_version?: string;
      running_order?: string[];
    }
  | {
      type: "methodology_progress";
      session_id?: string;
      status?: string;
      completed_step_ids?: string[];
      completed_agents?: string[];
      completed_phases?: string[];
      failed_step_ids?: string[];
      current_step_id?: string;
      current_agent?: string;
      strand_id?: string;
      strand_phase?: string;
      strand_ids?: string[];
      multi_strand_status?: string;
      dominance_band?: string;
      errors?: string[];
      is_multi_strand?: boolean;
    }
  | { type: "error"; message: string }
  | { type: "cancelled"; session_id?: string; message?: string }
  /** Emitted when the SSE stream drops but analysis continues; mirrors GET /status completed_agents */
  | { type: "progress_sync"; completed_agents: string[]; completed_phases?: string[] };
